/**
 * Integrations view — the bulwark.
 *
 * Built on the real Fern API spec ontology:
 *   IntegrationSource: agentmail, nim, stripe, notion, posthog, cloudflare, vercel
 *
 * Each integration has its own lifecycle, health surface, failure tracking,
 * and configuration. These are essential infrastructure — not the moat, not
 * the whole product, but required on every build.
 *
 * The envoy portal already has complete architecture for most of these.
 * This view unifies them into one surface with Fern-derived categories.
 */

import { useState, useEffect, useCallback } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";
import {
  INTEGRATIONS, INTEGRATION_ORDER,
  type IntegrationSource, type IntegrationMeta,
  type IntegrationFailure,
} from "../lib/integration-ontology";

interface IntegrationsViewProps {
  isOperator: boolean;
}

export function IntegrationsView({ isOperator }: IntegrationsViewProps) {
  const [activeSource, setActiveSource] = useState<IntegrationSource>("agentmail");
  const [failures, setFailures] = useState<IntegrationFailure[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [domainData, setDomainData] = useState<any[]>([]);
  const [inboxData, setInboxData] = useState<any[]>([]);
  const [billingData, setBillingData] = useState<any>(null);
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [cronState, setCronState] = useState<any>(null);
  const [notionDbs, setNotionDbs] = useState<any[]>([]);
  const [hermesStatus, setHermesStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadAll = useCallback(async (force: boolean = false) => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }
    if (force) setRefreshing(true);

    try {
      // Fetch all integration data in parallel — each endpoint is a Fern-defined service
      const results = await Promise.allSettled([
        clients.envoy.getBilling(),                    // stripe
        clients.envoy.getAgentConfig(),                // nim
        clients.envoy.getCronStatus(),                 // cloudflare (cron health)
        clients.envoy.getAgentContext(),               // agent context (tenant + inboxes)
        clients.envoy.getNotionDatabases(),            // notion
      ]);

      const [billing, agent, cron, ctx, notion] = results;
      if (billing.status === "fulfilled") setBillingData(applyMembrane(billing.value, isOperator));
      if (agent.status === "fulfilled") setAgentConfig(applyMembrane(agent.value, isOperator));
      if (cron.status === "fulfilled") setCronState(cron.value);

      // Fetch Hermes M4 transport status
      try {
        const hermes = await clients.control.getHermesStatus();
        setHermesStatus(hermes);
      } catch {}

      if (ctx.status === "fulfilled") {
        const ctxVal = ctx.value as any;
        // Domains and inboxes come from the tenant context
        setInboxData(applyMembrane(ctxVal?.inboxes || [], isOperator));
        setDomainData(applyMembrane(ctxVal?.domains || [], isOperator));
      }
      if (notion.status === "fulfilled") {
        setNotionDbs(applyMembrane((notion.value as any)?.databases || [], isOperator));
      }

      // Fetch alerts and integration failures (operator-level)
      if (isOperator) {
        try {
          const alertRes: any = await clients.envoy.listEscalations({ status: "OPEN" });
          setAlerts(applyMembrane(alertRes?.escalations || [], isOperator));
        } catch {}
      }

      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isOperator]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const membraneBypass = isOperator;
  const meta = INTEGRATIONS[activeSource];

  if (loading) return <div className="loading">Loading integrations…</div>;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">Infrastructure</span>
        <h1>Integrations</h1>
        <button className="btn-secondary" onClick={() => loadAll(true)} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <div className="error-state">{error}</div>}

      {/* Integration source grid — Fern IntegrationSource enum */}
      <div className="integration-grid">
        {INTEGRATION_ORDER.map((source) => {
          const m = INTEGRATIONS[source];
          const sourceFailures = failures.filter((f) => f.source === source);
          const sourceAlerts = alerts.filter((a) => {
            // Map alert categories to integration sources
            const cat = (a.category || "").toLowerCase();
            if (cat.includes("send") || cat.includes("webhook")) return source === "agentmail";
            if (cat.includes("nim")) return source === "nim";
            if (cat.includes("stripe")) return source === "stripe";
            if (cat.includes("cron")) return source === "cloudflare";
            return false;
          });
          const hasIssues = sourceFailures.length > 0 || sourceAlerts.length > 0;
          const isOperatorOnly = m.managedBy === "operator" && !isOperator;

          return (
            <button
              key={source}
              className={`integration-card ${activeSource === source ? "active" : ""} ${hasIssues ? "has-issues" : ""} ${isOperatorOnly ? "operator-only" : ""}`}
              onClick={() => !isOperatorOnly && setActiveSource(source)}
              disabled={isOperatorOnly}
            >
              <span className="integration-icon">{m.icon}</span>
              <div className="integration-info">
                <div className="integration-label">{applyMembrane(m.label, membraneBypass)}</div>
                <div className="integration-source mono">{source}</div>
              </div>
              {hasIssues && <span className="integration-badge badge" data-status="error">{sourceFailures.length + sourceAlerts.length}</span>}
              {isOperatorOnly && <span className="integration-lock">🔒</span>}
            </button>
          );
        })}
      </div>

      {/* Active integration detail */}
      <div className="integration-detail">
        <IntegrationDetail
          meta={meta}
          isOperator={isOperator}
          domainData={domainData}
          inboxData={inboxData}
          billingData={billingData}
          agentConfig={agentConfig}
          cronState={cronState}
          notionDbs={notionDbs}
          failures={failures.filter((f) => f.source === meta.source)}
          alerts={alerts}
        />
      </div>

      {/* Hermes M4 transport status */}
      {hermesStatus && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="card-title">Hermes M4 Transport</div>
          <div className="object-fields" style={{ marginTop: 8 }}>
            <div className="object-field">
              <span className="object-field-label">Gateway</span>
              <span className="object-field-value">
                <span className="badge" data-status={hermesStatus.gateway_running ? "active" : "inactive"}>
                  {hermesStatus.gateway_running ? "Running" : "Stopped"}
                </span>
              </span>
            </div>
            {hermesStatus.transports && Object.entries(hermesStatus.transports).map(([name, status]: [string, any]) => (
              <div key={name} className="object-field">
                <span className="object-field-label">{name}</span>
                <span className="object-field-value">
                  <span className="badge" data-status={status === "active" ? "active" : "pending"}>{status}</span>
                </span>
              </div>
            ))}
            <div className="object-field">
              <span className="object-field-label">F4 Adjudicator</span>
              <span className="object-field-value">
                <span className="badge" data-status={hermesStatus.f4_adjudicator === "configured" ? "active" : "inactive"}>
                  {hermesStatus.f4_adjudicator}
                </span>
              </span>
            </div>
            {hermesStatus.last_alert && (
              <div className="object-field">
                <span className="object-field-label">Last Alert</span>
                <span className="object-field-value">{hermesStatus.last_alert}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Integration Detail Panel ───────────────────────────────────────────────
function IntegrationDetail({
  meta, isOperator, domainData, inboxData, billingData, agentConfig, cronState, notionDbs, failures, alerts,
}: {
  meta: IntegrationMeta;
  isOperator: boolean;
  domainData: any[];
  inboxData: any[];
  billingData: any;
  agentConfig: any;
  cronState: any;
  notionDbs: any[];
  failures: IntegrationFailure[];
  alerts: any[];
}) {
  const membraneBypass = isOperator;

  return (
    <div className="panel">
      {/* Header */}
      <div className="integration-detail-header">
        <span className="integration-icon large">{meta.icon}</span>
        <div>
          <h2>{applyMembrane(meta.label, membraneBypass)}</h2>
          <p className="muted">{meta.description}</p>
          <div className="integration-meta-row">
            <span className="badge">{meta.category}</span>
            <span className="muted">Managed by: {meta.managedBy}</span>
            <span className="muted">Services: {meta.services.join(", ") || "—"}</span>
          </div>
        </div>
      </div>

      {/* Failures / issues */}
      {failures.length > 0 && (
        <div className="card">
          <div className="card-title">Issues ({failures.length})</div>
          {failures.map((f, i) => (
            <div key={i} className="failure-row">
              <span className="badge" data-status={f.severity.toLowerCase()}>{f.severity}</span>
              <span className="failure-op">{f.operation}</span>
              <span className="failure-status badge" data-status={f.status.toLowerCase()}>{f.status}</span>
              {f.last_error && <span className="failure-error muted">{f.last_error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Source-specific detail */}
      {meta.source === "agentmail" && <AgentmailDetail domains={domainData} inboxes={inboxData} isOperator={isOperator} />}
      {meta.source === "nim" && <NimDetail agentConfig={agentConfig} isOperator={isOperator} />}
      {meta.source === "stripe" && <StripeDetail billing={billingData} isOperator={isOperator} />}
      {meta.source === "notion" && <NotionDetail dbs={notionDbs} isOperator={isOperator} />}
      {meta.source === "cloudflare" && <CloudflareDetail cronState={cronState} isOperator={isOperator} />}
      {meta.source === "posthog" && <PosthogDetail isOperator={isOperator} />}
      {meta.source === "vercel" && <VercelDetail isOperator={isOperator} />}
    </div>
  );
}

// ── Agentmail Detail (email infra) ─────────────────────────────────────────
function AgentmailDetail({ domains, inboxes, isOperator }: { domains: any[]; inboxes: any[]; isOperator: boolean }) {
  return (
    <>
      <div className="card">
        <div className="card-title">Domains ({domains.length})</div>
        {domains.length === 0 ? <div className="empty-state">No domains configured.</div> : (
          <table className="data-table">
            <thead><tr><th>Domain</th><th>Role</th><th>DNS</th><th>Provision</th></tr></thead>
            <tbody>
              {domains.map((d, i) => (
                <tr key={i}>
                  <td>{d.domain}</td>
                  <td><span className="badge" data-status={d.domain_role}>{d.domain_role}</span></td>
                  <td><span className="badge" data-status={(d.dns_status || "").toUpperCase()}>{d.dns_status || "PENDING"}</span></td>
                  <td>{d.provision_type || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="card">
        <div className="card-title">Inboxes ({inboxes.length})</div>
        {inboxes.length === 0 ? <div className="empty-state">No inboxes provisioned.</div> : (
          <table className="data-table">
            <thead><tr><th>Address</th><th>Type</th><th>Warmup</th><th>Sends Today</th><th>Daily Limit</th></tr></thead>
            <tbody>
              {inboxes.map((ibx, i) => (
                <tr key={i}>
                  <td>{ibx.inbox_address || ibx.email}</td>
                  <td><span className="badge" data-status={ibx.inbox_type}>{ibx.inbox_type}</span></td>
                  <td><span className="badge" data-status={(ibx.warmup_stage || "NEW").toUpperCase()}>{ibx.warmup_stage || "NEW"}</span></td>
                  <td>{ibx.sends_today ?? 0}</td>
                  <td>{ibx.daily_send_limit ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── NIM Detail (AI model service) ──────────────────────────────────────────
function NimDetail({ agentConfig, isOperator }: { agentConfig: any; isOperator: boolean }) {
  return (
    <div className="card">
      <div className="card-title">AI Configuration</div>
      <div className="object-fields">
        <div className="object-field"><span className="object-field-label">Model</span><span className="object-field-value">{agentConfig?.nim_model || "default"}</span></div>
        <div className="object-field"><span className="object-field-label">Agent Mode</span><span className="object-field-value"><span className="badge" data-status={agentConfig?.mode || "STEALTH"}>{agentConfig?.mode || "STEALTH"}</span></span></div>
        <div className="object-field"><span className="object-field-label">Tools Enabled</span><span className="object-field-value">{(agentConfig?.tools_enabled || []).join(", ") || "—"}</span></div>
      </div>
    </div>
  );
}

// ── Stripe Detail (billing) ────────────────────────────────────────────────
function StripeDetail({ billing, isOperator }: { billing: any; isOperator: boolean }) {
  if (!billing) return <div className="empty-state">Billing data not available.</div>;
  return (
    <>
      <div className="card">
        <div className="card-title">Plan</div>
        <div className="object-fields">
          <div className="object-field"><span className="object-field-label">Tier</span><span className="object-field-value"><span className="badge" data-status="active">{billing.tier || "—"}</span></span></div>
          <div className="object-field"><span className="object-field-label">Status</span><span className="object-field-value"><span className="badge" data-status={(billing.status || "").toUpperCase()}>{billing.status || "INACTIVE"}</span></span></div>
          <div className="object-field"><span className="object-field-label">Period Start</span><span className="object-field-value">{billing.current_period_start || "—"}</span></div>
          <div className="object-field"><span className="object-field-label">Period End</span><span className="object-field-value">{billing.current_period_end || "—"}</span></div>
        </div>
      </div>
      <div className="card">
        <div className="card-title">Usage This Period</div>
        <div className="object-fields">
          <div className="object-field"><span className="object-field-label">Emails Sent</span><span className="object-field-value">{billing.usage_this_period?.emails_sent ?? 0}</span></div>
          <div className="object-field"><span className="object-field-label">Agent Runs</span><span className="object-field-value">{billing.usage_this_period?.agent_runs ?? 0}</span></div>
        </div>
      </div>
      <div className="card">
        <div className="card-title">Credits</div>
        <div className="object-fields">
          <div className="object-field"><span className="object-field-label">Balance</span><span className="object-field-value">{billing.credits?.balance ?? "—"}</span></div>
        </div>
      </div>
    </>
  );
}

// ── Notion Detail (knowledge base) ─────────────────────────────────────────
function NotionDetail({ dbs, isOperator }: { dbs: any[]; isOperator: boolean }) {
  return (
    <div className="card">
      <div className="card-title">Connected Databases</div>
      {dbs.length === 0 ? (
        <div className="empty-state">
          <p>No Notion databases connected.</p>
          <p className="muted">Connect Notion to give your agent access to business context.</p>
        </div>
      ) : (
        dbs.map((db, i) => (
          <div key={i} className="card-list-item">
            <span className="card-title">{db.name || db.title}</span>
            <span className="muted mono">{db.id}</span>
          </div>
        ))
      )}
    </div>
  );
}

// ── Cloudflare Detail (edge & cron) ────────────────────────────────────────
function CloudflareDetail({ cronState, isOperator }: { cronState: any; isOperator: boolean }) {
  const crons: any[] = cronState?.crons || [];
  return (
    <div className="card">
      <div className="card-title">Scheduled Services ({crons.length})</div>
      {crons.length === 0 ? <div className="empty-state">No scheduled services.</div> : (
        crons.map((cron, i) => {
          const hasError = cron.error_count > 0;
          return (
            <div key={i} className="cron-row">
              <div className="cron-header">
                <span className="cron-name">{cron.name || cron.cron_id}</span>
                <span className={`badge`} data-status={hasError ? "error" : "active"}>{hasError ? "Errors" : "Healthy"}</span>
              </div>
              <div className="cron-meta muted">
                Runs: {cron.run_count ?? 0} · Errors: {cron.error_count ?? 0} · Last: {cron.last_run || "—"}
              </div>
              {cron.last_error && <div className="error-state">{cron.last_error}</div>}
            </div>
          );
        })
      )}
    </div>
  );
}

// ── PostHog Detail (analytics) ─────────────────────────────────────────────
function PosthogDetail({ isOperator }: { isOperator: boolean }) {
  return (
    <div className="card">
      <div className="card-title">Product Analytics</div>
      <p className="muted">Event tracking is configured at the operator level. Events are captured via the PostHog integration.</p>
      {!isOperator && <p className="muted">Contact your operator for analytics access.</p>}
    </div>
  );
}

// ── Vercel Detail (deployment) ─────────────────────────────────────────────
function VercelDetail({ isOperator }: { isOperator: boolean }) {
  return (
    <div className="card">
      <div className="card-title">Deployment</div>
      <p className="muted">Frontend deployment is managed by the operator. Portal updates are deployed automatically.</p>
    </div>
  );
}
